import { GetServerSidePropsContext } from 'next';
import { PostHog } from 'posthog-node';
import { ExperimentFlags, FeatureFlags, FlagBagType } from '../context/flagging';
import { handleAnonID } from './cookies';
import { createClient, retrieveExperimentalFlag, retrieveFeatureFlag } from './postHog';

// =========   Flags to edit   ============
//
// Add feature flag keys here for feature flags
export const featureKeys = {
  placeholder: 'placeholder', // This is just a placeholder, comment it out when adding a new flag
};

// =========   Commandments for Experiment AB tests   ============
//
// 1. Thou shalt only have 2 AB tests in the code at any given time
// 2. Once a test is complete, remove it from the code immediately or face divine intervention
// 3. Thy AB tests in the code should not have any overlap
// 4. Thy AB tests are only to be built off the default control of the website
//
// Add experiment feature flags here for AB tests

export const experimentKeys = {
  hideUnitsSoldExperiment: 'hide_units_sold_experiment_v2',
};

export const retrievePosthogFlagBagServerSide = async (ctx: GetServerSidePropsContext): Promise<FlagBagType> => {
  const emptyFlags: FeatureFlags = {};
  const emptyExperiments: ExperimentFlags = {};
  let currentAnonID: string;
  let client: PostHog | undefined;

  try {
    currentAnonID = await handleAnonID(ctx);
    client = createClient();
  } catch {
    return { featureFlags: emptyFlags, experimentFlags: emptyExperiments };
  }

  const onFlagTimeout = new Promise<FeatureFlags>((resolve) => setTimeout(() => resolve(emptyFlags), 2_000));
  const onExperimentTimeout = new Promise<ExperimentFlags>((resolve) =>
    setTimeout(() => resolve(emptyExperiments), 2_000),
  );

  try {
    const featureFlags = getPosthogFeatureFlagsServerSide(currentAnonID, client);
    const experimentFlags = getPosthogExperimentFlagsServerSide(currentAnonID, client);

    const flags = await raceTwoPromises(featureFlags, onFlagTimeout);
    const experiments = await raceTwoPromises(experimentFlags, onExperimentTimeout);

    return {
      featureFlags: flags,
      experimentFlags: experiments,
    };
  } catch {
    return { featureFlags: emptyFlags, experimentFlags: emptyExperiments };
  }
};

const raceTwoPromises = <T>(promise1: Promise<T>, promise2: Promise<T>): Promise<T> =>
  Promise.race([promise1, promise2]);

const getPosthogFeatureFlagsServerSide = async (currentAnonID: string, client: PostHog): Promise<FeatureFlags> => {
  let key: keyof typeof featureKeys;
  const featureFlags: FeatureFlags = {};

  for (key in featureKeys) {
    if (Object.prototype.hasOwnProperty.call(featureKeys, key)) {
      // eslint-disable-next-line no-await-in-loop
      const flag = await retrieveFeatureFlag(featureKeys[key], currentAnonID, client);
      featureFlags[featureKeys[key]] = flag;
    }
  }

  return featureFlags;
};

const getPosthogExperimentFlagsServerSide = async (
  currentAnonID: string,
  client: PostHog,
): Promise<ExperimentFlags> => {
  let key: keyof typeof experimentKeys;
  const experimentFlags: ExperimentFlags = {};

  for (key in experimentKeys) {
    if (Object.prototype.hasOwnProperty.call(experimentKeys, key)) {
      // eslint-disable-next-line no-await-in-loop
      const flag = await retrieveExperimentalFlag(experimentKeys[key], currentAnonID, client);
      experimentFlags[experimentKeys[key]] = flag;
    }
  }

  await client.shutdownAsync();

  return experimentFlags;
};
